import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Check } from 'lucide-react';
import OfferContractModal from './OfferContractModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Brand palette — noir + orange + vert succès
const COL = {
  orange:     '#F05A1A',
  orangeDark: '#D94D0F',
  orangeSoft: 'rgba(240,90,26,0.10)',
  green:      '#10b981',
  greenDark:  '#059669',
  greenSoft:  'rgba(16,185,129,0.10)',
  black:      '#1A1A1A',
  blackSoft:  '#2A2A2A',
  cream:      '#FAF6F0',
  border:     'rgba(26,26,26,0.10)',
  textMuted:  'rgba(26,26,26,0.58)',
};

const PACKS = {
  audit_day: {
    id: 'audit_day',
    label: '1 Day',
    badge: 'NOUVEAU',
    subLabel: 'Audit terrain · 1 journée',
    title: '1 Day — Audit terrain',
    titleHighlight: 'sur place avec Sabril',
    price: 2000,
    paymentType: 'audit_day',
    onsiteBadge: '1J',
    onsiteText: '1 journée sur place (8h)',
    onsiteSubText: 'Audit terrain 360° · pas d\'engagement long',
    scarcity: { total: 3, taken: 1, period: 'ce mois-ci', color: 'orange' },
    stats: [
      { big: '1J', label: 'sur place' },
      { big: '48H', label: 'plan d\'action' },
      { big: 'J+30', label: 'débrief Zoom' },
    ],
    items: [
      { title: '1 journée terrain (8h) sur place', detail: 'Sabril vient dans ton resto pour observer en direct · service · équipe · cuisine · clients' },
      { title: 'Audit 360° en live', detail: 'Cuisine · service · process · chiffres · carte · digital — rien n\'est laissé de côté' },
      { title: 'Plan d\'action écrit sous 48h', detail: 'Document PDF détaillé livré 48h après la journée · priorités classées par impact €' },
      { title: 'Débrief Zoom 30 min à J+30', detail: 'On regarde ensemble ce que tu as appliqué · ce qui bloque · les prochains caps' },
      { title: '🎁 Déduction Refonte', detail: 'Si tu prends la Refonte dans les 60 jours, les 2 000€ de l\'audit terrain sont déduits du tarif final' },
      { title: '🚀 Dashboard de Pilotage', detail: 'Accès au cockpit personnel (KPIs temps réel · plan d\'action interactif · objectifs hebdo) · BIENTÔT disponible · inclus' },
    ],
    notIncluded: 'Pas d\'accompagnement long · Pas de calls hebdo · Pas de retravail de carte/identité · Tu exécutes seul après le plan d\'action',
    footer: '🎯 L\'offre idéale pour tester le travail avec Sabril avant Refonte ou Ouverture. Audit terrain concret + plan actionnable — et si tu veux aller plus loin, les 2 000€ sont déduits.',
  },
  refonte: {
    id: 'refonte',
    label: 'Refonte',
    subLabel: 'Restos ≥ 30 000€/mois',
    title: 'Refonte complète',
    titleHighlight: 'en 90 jours',
    price: 6700,
    paymentType: 'full',
    onsiteBadge: '2J',
    onsiteText: '2 jours sur place chez toi',
    onsiteSubText: 'Audit terrain · équipe · cuisine — avec toi',
    scarcity: { total: 2, taken: 0, period: 'ce mois-ci', color: 'orange' },
    stats: [
      { big: '2J', label: 'sur place' },
      { big: '90', label: 'jours' },
      { big: '10', label: 'modules' },
    ],
    items: [
      { title: 'On travaille tes chiffres', detail: 'Objectif 25-30% food cost · 30% masse salariale' },
      { title: 'On retravaille ta carte', detail: '1 plat star · menu phare tendance 2026 · carte rentable' },
      { title: 'On bosse ton image de marque', detail: 'Direction artistique · cohérence visuelle' },
      { title: 'Validation de tes prestataires', detail: 'On checke devis, contrats, fournisseurs, agences — on évite les arnaques' },
      { title: 'Stratégie réseaux sociaux', detail: '+15% de CA · calendrier 3 mois + scripts viraux' },
      { title: 'Fiches techniques', detail: 'Dosages parfaits · plats calibrés au gramme près' },
      { title: 'Recrutement & onboarding', detail: 'Doc onboarding · semaine type nouveau profil · primes performance' },
      { title: 'Organisation interne', detail: 'QR code à table · borne · click & collect · outils malins' },
      { title: 'Coaching dirigeant', detail: '45 min tous les 15 jours pendant 3 mois (prio · stratégie · recrutement)' },
      { title: 'Stratégie livraison', detail: 'Uber Eats / Deliveroo · carte · prix · offres' },
      { title: 'Packaging mockups', detail: 'Packaging tendance unique adapté à ta DA' },
      { title: '+ 2 jours sur place', detail: 'Audit terrain · équipe · service · cuisine, en direct avec toi' },
      { title: '🚀 Dashboard de Pilotage', detail: 'Cockpit personnel : KPIs temps réel · plan d\'action interactif semaine par semaine · suivi métriques business · BIENTÔT disponible · inclus' },
    ],
    notIncluded: 'Pas de prod photo/vidéo · Pas de site internet inclus',
    footer: '🛡️ Engagement long terme. On construit une marque pilotée comme un industriel — durable, duplicable, rentable par sa croissance. Pas de promesse magique, pas de gimmick : que du concret affûté sur 7 marchés exigeants (Paris, Cannes, Marrakech, Bangtao, New York, Miami, Marbella).',
  },
  ouverture: {
    id: 'ouverture',
    label: 'Ouverture',
    badge: 'PRODUIT SIGNATURE',
    subLabel: 'Concept premium sérieux · le projet le plus rare',
    title: 'Ouverture de A à Z',
    titleHighlight: 'sur 3 mois',
    price: 14900,
    paymentType: 'opening',
    onsiteBadge: '9J',
    onsiteText: '3 jours / mois × 3 mois',
    onsiteSubText: '9 jours total · avant + pendant + après ouverture',
    scarcity: { total: 4, taken: 3, period: 'ce mois-ci', color: 'red' },
    stats: [
      { big: '9J', label: 'sur place' },
      { big: '3', label: 'mois' },
      { big: '13', label: 'modules' },
    ],
    items: [
      { title: 'Concept · naming · positionnement', detail: 'On définit qui tu es avant même d\'ouvrir' },
      { title: 'Identité visuelle complète', detail: 'Logo · charte graphique · direction artistique' },
      { title: 'Design intérieur · conseils déco', detail: 'Cohérence visuelle de la salle · ambiance' },
      { title: 'Packaging design + mockups', detail: 'Sacs · boîtes · étiquettes · supports' },
      { title: 'Validation de tes prestataires', detail: 'On checke devis, contrats, artisans, fournisseurs — on évite les arnaques (8-15k€ d\'économies)' },
      { title: 'Carte + menu gagnant', detail: 'Food cost optimal · prix dès J1 · pas d\'erreur' },
      { title: 'Recrutement + formation équipe', detail: 'Choix des profils · onboarding · process avant J1' },
      { title: 'Organisation interne complète', detail: 'Planning · briefings · fiches techniques · process' },
      { title: 'Stratégie réseaux sociaux', detail: 'Insta + TikTok · calendrier + scripts (la stratégie, pas la prod)' },
      { title: 'Stratégie d\'opening', detail: 'Soft opening · jour J · buzz · presse · influence (tu exécutes)' },
      { title: 'Choix emplacement', detail: 'Analyse zone de chalandise (on t\'oriente, tu négocies)' },
      { title: 'Setup food cost / prime cost', detail: 'Package clé en main · ratios pilotés' },
      { title: '+ 3j sur place / mois × 3 mois', detail: '9 jours total avec toi · avant + pendant + après' },
      { title: '🚀 Dashboard de Pilotage', detail: 'Cockpit personnel dès J1 d\'ouverture : KPIs temps réel · plan d\'action interactif · suivi métriques · BIENTÔT disponible · inclus' },
    ],
    notIncluded: 'Pas de prod photo/vidéo · Pas de négo bail · Pas de site internet inclus · Tu exécutes (presse, influenceurs, soft opening)',
    footer: '⚡ Les restaurateurs qui ouvrent sans accompagnement perdent en moyenne 50 000€ la 1ère année. On évite ça. Objectif marque : ouvrir avec un modèle déjà pensé duplicable et franchisable.',
  },
  groupe: {
    id: 'groupe',
    label: 'Groupe',
  subLabel: 'Groupes & multi-resto (3+ établissements)',
    title: 'Accompagnement Groupe',
    titleHighlight: 'sur 6 mois',
    price: 29000,
    paymentType: 'groupe',
    onsiteBadge: '12J',
    onsiteText: '2 jours sur place / mois · 6 mois',
    onsiteSubText: '12 jours total répartis sur tes établissements',
    scarcity: { total: 2, taken: 0, period: 'tous les 6 mois', color: 'red' },
    stats: [
      { big: '12J', label: 'sur place' },
      { big: '6', label: 'mois' },
      { big: '3+', label: 'restos' },
    ],
    items: [
      { title: 'Audit complet de tes établissements', detail: 'Diagnostic financier, opérationnel, RH sur chaque resto · benchmark interne' },
      { title: 'Uniformisation des process', detail: 'Mêmes recettes, mêmes ratios, même qualité partout' },
      { title: 'Charte managériale Groupe', detail: 'Onboarding, fiches de poste, briefings types · structure managers' },
      { title: 'Pilotage central via Audit Resto', detail: 'Cockpit multi-restos : tu vois tout, partout, en direct' },
      { title: 'Stratégie de croissance', detail: 'Quel resto pousser, lequel restructurer, lequel céder' },
      { title: 'Negociation centrale fournisseurs', detail: 'Volume groupé · économies 8 à 15% sur les achats' },
      { title: 'Coaching dirigeant + managers', detail: '2 calls/mois avec toi + 1 call/mois avec ton bras droit' },
      { title: 'Plan réseaux groupé', detail: 'Stratégie commune marque-mère + déclinaisons par resto' },
      { title: 'Direction artistique cohérente', detail: 'Identité Groupe forte (sans uniformiser bêtement)' },
      { title: 'Clause confidentialité bilatérale', detail: 'NDA signée — tes chiffres et tes méthodes restent entre nous' },
      { title: '+ 2j sur place/mois × 6 mois', detail: '12 jours total répartis sur tes points de vente' },
      { title: '🚀 Dashboard de Pilotage multi-restos', detail: 'Cockpit central : KPIs temps réel de chaque resto · plan d\'action interactif par site · benchmark interne · BIENTÔT disponible · inclus' },
    ],
    notIncluded: 'Pas de prod photo/vidéo · Pas de site internet inclus · Tu gardes la main exécutive',
    footer: '🏢 Pensé pour les groupes et multi-restos (3 établissements et +) qui veulent structurer avant de scaler. ROI cible : 60-90 jours. Objectif marque : modèle industriel consolidé, prêt pour la franchise et l\'international.',
  },
};

export const PricingTabs = ({ initialTab = 'ouverture', highlightTabId = null, hidePrice = false }) => {
  const [active, setActive] = useState(initialTab);
  const [addWebsite, setAddWebsite] = useState({ refonte: false, ouverture: false, groupe: false, audit_day: false });
  const [addAuditApp, setAddAuditApp] = useState({ refonte: false, ouverture: false, groupe: false, audit_day: false });
  const [addFranchise, setAddFranchise] = useState({ refonte: false, ouverture: false, groupe: false, audit_day: false });
  const [addExtension, setAddExtension] = useState({ refonte: false, ouverture: false, groupe: false, audit_day: false });
  const [contractOffer, setContractOffer] = useState(null);

  const pack = PACKS[active];
  const isDark = active === 'ouverture';
  const isAuditDay = active === 'audit_day';
  const websiteAddon = addWebsite[active] && pack.price && !isAuditDay ? 690 : 0;
  const franchiseAddon = active === 'refonte' && addFranchise.refonte ? 3900 : 0;
  const extensionAddon = addExtension[active] && pack.price && !isAuditDay ? 3900 : 0;
  const total = (pack.price || 0) + websiteAddon + franchiseAddon + extensionAddon;
  // Audit Resto SaaS — recurring, displayed separately (not added to one-shot total)
  const auditAppMonthly = addAuditApp[active] && !isAuditDay ? 229 : 0;

  // Addons available on Refonte / Ouverture / Groupe — NOT on the 1 Day offer
  const addonsAvailable = !isAuditDay;

  const handleChooseOffer = () => {
    // Build the list of activated paid options (for the "Ce qu'on fait" section)
    const activatedOptions = [];
    if (addAuditApp[active]) activatedOptions.push({ title: '📱 App Audit Resto — Suite SaaS', detail: '6 agents IA (Hugo, Sophie, Marco, Claire, Léo, Sabril) · abonnement séparé 229€/mois (résiliable à tout moment)' });
    if (addWebsite[active]) activatedOptions.push({ title: '🌐 Site internet', detail: 'Design + dev + mise en ligne · +690€ TTC' });
    if (active === 'refonte' && addFranchise.refonte) activatedOptions.push({ title: '🚀 Modèle Franchise / Duplicable', detail: 'Format duplicable · automatisation 360 · DIP · +3 900€ TTC' });
    if (addExtension[active]) activatedOptions.push({ title: '🔄 Coaching prolongé +3 mois', detail: 'Continuité après la mission · +3 900€ TTC' });

    const offer = {
      id: pack.id,
      name: pack.title + (pack.titleHighlight ? ` — ${pack.titleHighlight}` : ''),
      price: pack.price ? total : null,
      engagement: pack.id === 'refonte' ? '90 jours · 2j sur place'
                 : pack.id === 'ouverture' ? '3 mois · 9j sur place'
                 : pack.id === 'groupe' ? '6 mois · 12j sur place répartis'
                 : pack.id === 'audit_day' ? '1 journée terrain + plan d\'action 48h + débrief J+30'
                 : null,
      mode: pack.price ? 'one_shot' : 'discovery',
      payment_terms: pack.price ? 'paiement à la signature (CB Stripe, virement bancaire ou sur place)' : null,
      conditions: [
        'Documents transmis : contrat signé numériquement + facture',
        'Si paiement par virement : le travail démarre à la réception du virement',
        'Si paiement sur place : le travail démarre lors du 1er rendez-vous, après règlement',
        ...(addAuditApp[active] ? ['Option App Audit Resto activée : abonnement SaaS 229€/mois facturé séparément via Stripe (résiliable à tout moment)'] : []),
      ],
      // Pass full scope for display inside contract modal (above signature)
      scopeIncluded: pack.items || [],
      scopeOptionsActivated: activatedOptions,
      scopeExcluded: pack.notIncluded || '',
      addOptions: {
        audit_app_saas: !!addAuditApp[active],
        website: !!addWebsite[active],
        franchise_model: active === 'refonte' && !!addFranchise.refonte,
        extension_3m: !!addExtension[active],
      },
    };
    setContractOffer(offer);
  };

  // Legacy direct-checkout (kept for fallback / Stripe Payment Link if needed)
  const [loading, setLoading] = useState(false);
  const handleStripeCheckout = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/prsnl/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          origin_url: window.location.origin,
          payment_type: pack.paymentType,
          add_website: addWebsite[active],
          add_audit_app: addAuditApp[active],
          add_franchise: active === 'refonte' && addFranchise.refonte,
          add_extension_3m: addExtension[active],
        }),
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch (e) {
      console.error(e);
      setLoading(false);
    }
  };
  void handleStripeCheckout;

  return (
    <div className="max-w-2xl mx-auto" data-testid="pricing-tabs">
      {/* Bandeau de réassurance / urgence */}
      <div
        className="rounded-2xl p-3 sm:p-4 mb-4 grid grid-cols-3 gap-2 sm:gap-3"
        style={{ background: '#1a1a1a' }}
        data-testid="trust-strip"
      >
        <div className="text-center">
          <p className="font-bebas text-[#F05A1A] text-2xl sm:text-3xl leading-none">5 PAYS</p>
          <p className="text-white/70 text-[9px] sm:text-[10px] uppercase tracking-wider mt-0.5">Paris · Cannes · Marrakech · Bangtao · NY · Miami · Marbella</p>
        </div>
        <div className="text-center border-x border-white/10">
          <p className="font-bebas text-[#F05A1A] text-2xl sm:text-3xl leading-none">DURABLE</p>
          <p className="text-white/70 text-[9px] sm:text-[10px] uppercase tracking-wider mt-0.5">Marque pilotable · scalable · rentable par sa croissance</p>
        </div>
        <div className="text-center">
          <p className="font-bebas text-[#F05A1A] text-2xl sm:text-3xl leading-none">SÉLECTIF</p>
          <p className="text-white/70 text-[9px] sm:text-[10px] uppercase tracking-wider mt-0.5">Places limitées par offre · sélection sur validation</p>
        </div>
      </div>

      {/* Segmented control */}
      <div
        className="relative grid grid-cols-2 sm:grid-cols-4 p-1.5 rounded-2xl mb-4 mt-6 gap-1"
        style={{ background: 'rgba(26,26,26,0.06)' }}
        data-testid="pricing-segmented-control"
      >
        {Object.values(PACKS).map((p) => {
          const isActive = active === p.id;
          const isOuverture = p.id === 'ouverture';
          const activeBg = isOuverture ? COL.black : COL.orange;
          const activeFg = isOuverture ? COL.orange : '#fff';
          return (
            <button
              key={p.id}
              onClick={() => setActive(p.id)}
              className="relative z-10 py-3 px-2 rounded-xl transition-all"
              data-testid={`pricing-tab-${p.id}`}
              style={{ background: isActive ? activeBg : 'transparent', boxShadow: isActive ? '0 4px 12px rgba(0,0,0,0.12)' : 'none' }}
            >
              {p.badge && (
                <span
                  className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full text-[8px] sm:text-[9px] font-black uppercase tracking-wider whitespace-nowrap shadow-md"
                  style={{ background: '#1a1a1a', color: '#F05A1A', border: '1px solid #F05A1A' }}
                >
                  ★ {p.badge}
                </span>
              )}
              {highlightTabId === p.id && !p.badge && (
                <span
                  className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full text-[8px] sm:text-[9px] font-black uppercase tracking-wider whitespace-nowrap shadow-md"
                  style={{ background: '#10b981', color: '#fff', border: '1px solid #10b981' }}
                  data-testid={`pricing-recommended-${p.id}`}
                >
                  ⚡ POUR TOI
                </span>
              )}
              <span
                className="block leading-none font-black text-[10px] sm:text-xs uppercase tracking-widest"
                style={{ color: isActive ? activeFg : 'rgba(26,26,26,0.55)' }}
              >
                {p.label}
              </span>
              <span
                className="block text-[9px] sm:text-[10px] mt-1 font-bold tabular-nums"
                style={{ color: isActive ? 'rgba(255,255,255,0.85)' : 'rgba(26,26,26,0.45)' }}
              >
                {hidePrice ? 'Sur validation' : (p.price ? `${p.price.toLocaleString('fr-FR')}€` : 'Sur devis')}
              </span>
            </button>
          );
        })}
      </div>

      {/* Card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={active}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.25 }}
          className="rounded-3xl p-6 sm:p-7 shadow-2xl"
          style={{
            background: isDark ? `linear-gradient(135deg, ${COL.black} 0%, #0F0F0F 100%)` : '#fff',
            color: isDark ? '#fff' : COL.black,
            border: `2px solid ${isDark ? COL.orange + '99' : COL.orange}`,
          }}
          data-testid={`pricing-card-${active}`}
        >
          {/* Header */}
          <div className="mb-5">
            <div
              className="inline-block px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest mb-3"
              style={{
                background: isDark ? 'rgba(240,90,26,0.18)' : 'rgba(240,90,26,0.10)',
                color: COL.orange,
                border: `1px solid ${COL.orange}33`,
              }}
            >
              {pack.subLabel}
            </div>
            <h3 className="font-bebas text-3xl sm:text-4xl leading-none">
              {pack.title}
            </h3>
            <p className="font-bebas text-xl sm:text-2xl leading-none mt-1" style={{ color: COL.orange }}>
              {pack.titleHighlight}
            </p>
          </div>

          {/* Scarcity card — places limitées par offre */}
          {pack.scarcity && (
            <ScarcityCard scarcity={pack.scarcity} isDark={isDark} testid={`scarcity-${active}`} />
          )}

          {/* Stats trio */}
          <div className="grid grid-cols-3 gap-2 mb-5">
            {pack.stats.map((s, i) => (
              <div
                key={i}
                className="rounded-xl p-3 text-center"
                style={{
                  background: isDark ? 'rgba(255,255,255,0.05)' : COL.cream,
                  border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : COL.border}`,
                }}
              >
                <p className="font-bebas text-2xl sm:text-3xl leading-none" style={{ color: COL.orange }}>
                  {s.big}
                </p>
                <p className="text-[10px] mt-1 font-semibold uppercase tracking-wider" style={{ opacity: 0.6 }}>
                  {s.label}
                </p>
              </div>
            ))}
          </div>

          {/* On-site */}
          <div
            className="rounded-xl p-3 mb-6 flex items-center gap-3"
            style={{
              background: isDark ? 'rgba(240,90,26,0.12)' : 'rgba(240,90,26,0.08)',
              border: `1px solid ${COL.orange}33`,
            }}
          >
            <div
              className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center font-black text-white text-sm"
              style={{ background: `linear-gradient(135deg, ${COL.orange} 0%, ${COL.orangeDark} 100%)` }}
            >
              {pack.onsiteBadge}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold leading-tight" style={{ color: COL.orange }}>
                {pack.onsiteText}
              </p>
              <p className="text-[11px] mt-0.5 leading-snug" style={{ opacity: 0.7 }}>
                {pack.onsiteSubText}
              </p>
            </div>
          </div>

          {/* What we do — FEATURE CARDS — vert succès, dynamique et positif */}
          <p className="text-[10px] font-black uppercase tracking-widest mb-3 flex items-center gap-2" style={{ color: COL.green }}>
            <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#10b981]">
              <Check className="w-2.5 h-2.5 text-white" strokeWidth={4} />
            </span>
            Ce qu'on construit ensemble
          </p>
          <div className="grid gap-2 mb-6">
            {pack.items.map((it, i) => (
              <motion.div
                key={it.title}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.025, duration: 0.25 }}
                whileHover={{ y: -2, boxShadow: isDark ? '0 4px 12px rgba(16,185,129,0.20)' : '0 6px 18px rgba(16,185,129,0.15)' }}
                className="rounded-xl p-3 flex items-start gap-3 transition-colors"
                style={{
                  background: isDark ? 'rgba(255,255,255,0.04)' : '#fff',
                  border: `1.5px solid ${isDark ? 'rgba(16,185,129,0.25)' : 'rgba(16,185,129,0.30)'}`,
                  boxShadow: isDark ? 'none' : '0 1px 2px rgba(0,0,0,0.04)',
                }}
                data-testid={`pricing-item-${active}-${i}`}
              >
                <div
                  className="flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center mt-0.5 shadow-sm"
                  style={{ background: `linear-gradient(135deg, ${COL.green} 0%, ${COL.greenDark} 100%)` }}
                >
                  <Check className="w-4 h-4 text-white" strokeWidth={3.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-bold leading-tight" style={{ color: isDark ? '#fff' : COL.black }}>
                    {it.title}
                  </p>
                  <p className="text-[11px] leading-snug mt-0.5" style={{ opacity: 0.7 }}>
                    {it.detail}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Not included */}
          <div
            className="rounded-xl p-3 mb-5"
            style={{
              background: isDark ? 'rgba(255,255,255,0.04)' : COL.cream,
              border: `1px dashed ${isDark ? 'rgba(255,255,255,0.10)' : COL.border}`,
            }}
          >
            <p className="text-[10px] font-black uppercase tracking-widest mb-1" style={{ opacity: 0.55 }}>
              ⚠️ Ce qu'on ne fait pas
            </p>
            <p className="text-[11.5px] leading-snug" style={{ opacity: 0.7 }}>
              {pack.notIncluded}
            </p>
          </div>

          {/* Addons (only for one-shot offers) */}
          {addonsAvailable && (
            <div data-testid="addons-deck" className="mb-5">
              <p
                className="text-[10px] font-black uppercase tracking-[3px] mb-3"
                style={{ color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(26,26,26,0.55)' }}
              >
                ⚡ Options · à activer si pertinent
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <AddonCard
                  checked={addWebsite[active]}
                  onChange={(v) => setAddWebsite((p) => ({ ...p, [active]: v }))}
                  label="Site internet"
                  desc="Design + dev de ton site web"
                  price="+690€"
                  hidePrice={hidePrice}
                  isDark={isDark}
                  testId={`${active}-website-addon`}
                />
                <AddonCard
                  checked={addAuditApp[active]}
                  onChange={(v) => setAddAuditApp((p) => ({ ...p, [active]: v }))}
                  label="App Audit Resto"
                  desc="6 agents IA · finance · RH · achats · marketing"
                  price="229€/mois"
                  hidePrice={hidePrice}
                  isDark={isDark}
                  testId={`${active}-audit-app-addon`}
                />
                {active === 'refonte' && (
                  <AddonCard
                    checked={addFranchise.refonte}
                    onChange={(v) => setAddFranchise((p) => ({ ...p, refonte: v }))}
                    label="Modèle Franchise"
                    desc="Format duplicable · automatisation 360 · DIP"
                    price="+3 900€"
                    hidePrice={hidePrice}
                    isDark={isDark}
                    testId="refonte-franchise-addon"
                  />
                )}
                <AddonCard
                  checked={addExtension[active]}
                  onChange={(v) => setAddExtension((p) => ({ ...p, [active]: v }))}
                  label="Coaching prolongé +3 mois"
                  desc="Continuité après la mission · WhatsApp prio"
                  price="+3 900€"
                  hidePrice={hidePrice}
                  isDark={isDark}
                  testId={`${active}-extension-addon`}
                />
              </div>
            </div>
          )}

          {/* Price block — replaced by "Sur validation" panel when hidePrice */}
          {hidePrice ? (
            <div
              className="rounded-2xl p-5 mb-3 relative overflow-hidden"
              style={{ background: 'linear-gradient(135deg, #1A1A1A 0%, #2A1810 100%)' }}
              data-testid={`pricing-amount-${active}`}
            >
              {/* shimmer */}
              <motion.div
                className="absolute inset-0 pointer-events-none"
                style={{ background: 'linear-gradient(110deg, transparent 30%, rgba(240,90,26,0.10) 50%, transparent 70%)' }}
                animate={{ x: ['-100%', '100%'] }}
                transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
              />
              <div className="relative">
                <p className="text-[10px] font-black uppercase tracking-widest mb-1" style={{ color: '#F05A1A' }}>
                  🔒 Investissement révélé après échange
                </p>
                <p className="font-bebas text-white text-3xl sm:text-4xl leading-none mb-2">
                  Tarif sur validation
                </p>
                <p className="text-white/65 text-[12px] leading-snug">
                  Je veux d'abord comprendre <strong className="text-white">ton resto et ton projet</strong> avant de proposer un budget. <span className="text-white">15 min de chat avec Sabril</span>, pas de pitch — juste pour qualifier si on est faits pour bosser ensemble.
                </p>
              </div>
            </div>
          ) : (
            <div
              className="rounded-2xl p-5 mb-3"
              style={{ background: COL.orange }}
              data-testid={`pricing-amount-${active}`}
            >
              <div className="flex items-baseline justify-between gap-3 flex-wrap">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-white/80 mb-1">
                    {pack.price ? 'Investissement' : 'Tarif'}
                  </p>
                  <p className="font-bebas text-white text-5xl sm:text-6xl leading-none tabular-nums">
                    {pack.price ? `${total.toLocaleString('fr-FR')}€` : 'SUR DEVIS'}
                  </p>
                </div>
                {pack.price && (websiteAddon > 0 || franchiseAddon > 0 || extensionAddon > 0) && (
                  <p className="text-white/85 text-xs">
                    {pack.price.toLocaleString('fr-FR')}€
                    {websiteAddon ? ' + 690€ site' : ''}
                    {franchiseAddon ? ' + 3 900€ franchise' : ''}
                    {extensionAddon ? ' + 3 900€ extension 3m' : ''}
                  </p>
                )}
              </div>
              {auditAppMonthly > 0 && (
                <div className="mt-3 pt-3 border-t border-white/25 flex items-center justify-between gap-3 flex-wrap" data-testid={`audit-app-recap-${active}`}>
                  <p className="text-[10px] font-black uppercase tracking-widest text-white/80">📱 + App Audit Resto · récurrent</p>
                  <p className="text-white font-bebas text-2xl leading-none tabular-nums">+ {auditAppMonthly}€<span className="text-sm font-bold opacity-80">/mois</span></p>
                </div>
              )}
            </div>
          )}

          {/* CTA — opens contract modal OR Sabril Closer Bubble when hidePrice */}
          {hidePrice ? (
            <motion.button
              type="button"
              onClick={() => {
                try { window.dispatchEvent(new CustomEvent('sabril:open', { detail: { intent: pack.id === 'audit_day' ? '1day' : pack.id } })); } catch (e) { /* noop */ }
              }}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              className="w-full font-black py-4 rounded-2xl text-base flex items-center justify-center gap-3 transition-all shadow-lg uppercase tracking-wide"
              style={{ background: '#F05A1A', color: '#fff' }}
              data-testid={`pricing-talk-sabril-btn-${active}`}
            >
              Sélectionner cette offre
              <ArrowRight className="w-5 h-5" />
            </motion.button>
          ) : (
            <motion.button
              onClick={handleChooseOffer}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              className="w-full font-black py-4 rounded-2xl text-base flex items-center justify-center gap-3 transition-all shadow-lg uppercase tracking-wide"
              style={{ background: COL.black, color: '#fff' }}
              data-testid={`pricing-choose-offer-btn-${active}`}
            >
              {pack.price ? 'Je choisis cette offre' : 'Demander un appel'}
              <ArrowRight className="w-5 h-5" />
            </motion.button>
          )}

          <p className="text-[10px] text-center mt-2.5" style={{ opacity: 0.5 }}>
            🔒 {hidePrice ? `${pack.id === 'ouverture' ? '4 places/mois max' : pack.id === 'refonte' ? '2 places/mois max' : pack.id === 'groupe' ? '2 dossiers / semestre' : '3 places/mois'} · Sabril regarde ton dossier avant` : (pack.price ? 'Contrat signé en ligne · CB Stripe ou virement bancaire' : 'Aucun paiement à cette étape · discovery call 45 min')}
          </p>

          <p
            className="text-[11px] mt-5 leading-snug italic text-center"
            style={{ opacity: 0.55 }}
          >
            {pack.footer}
          </p>
        </motion.div>
      </AnimatePresence>

      <OfferContractModal
        open={!!contractOffer}
        onClose={() => setContractOffer(null)}
        offer={contractOffer}
      />
    </div>
  );
};

const ScarcityCard = ({ scarcity, isDark, testid }) => {
  const left = scarcity.total - scarcity.taken;
  const pct = Math.round((scarcity.taken / scarcity.total) * 100);
  // Couleur rouge = offre rare/premium (Ouverture, Groupe). 🔥 label = vraiment plus qu'une place.
  const isRare = scarcity.color === 'red';
  const isLastPlace = left <= 1;
  const accent = isRare ? '#ef4444' : '#F05A1A';
  const accentSoft = isRare ? 'rgba(239,68,68,0.10)' : 'rgba(240,90,26,0.10)';
  const label = isLastPlace
    ? '🔥 Dernière place'
    : isRare
      ? '💎 Offre rare'
      : '⚡ Places limitées';

  return (
    <div
      className="rounded-2xl p-4 mb-5 border-2"
      style={{
        background: isDark ? 'rgba(255,255,255,0.04)' : '#fff',
        borderColor: accent + (isDark ? '99' : ''),
      }}
      data-testid={testid}
    >
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-2">
          <span
            className={`inline-flex items-center justify-center w-2 h-2 rounded-full ${isLastPlace ? 'animate-pulse' : ''}`}
            style={{ background: accent, boxShadow: `0 0 0 4px ${accentSoft}` }}
          />
          <p className="text-[10px] font-black uppercase tracking-widest" style={{ color: accent }}>
            {label}
          </p>
        </div>
        <p className="text-[10px] font-black tabular-nums" style={{ color: isDark ? '#fff' : '#1a1a1a' }}>
          {left} / {scarcity.total}
        </p>
      </div>

      {/* Progress bar */}
      <div className="relative h-1.5 rounded-full overflow-hidden" style={{ background: isDark ? 'rgba(255,255,255,0.08)' : '#f5f3f0' }}>
        <div
          className="absolute inset-y-0 left-0 rounded-full transition-all"
          style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${accent} 0%, ${isRare ? '#dc2626' : '#FF7A3D'} 100%)` }}
        />
      </div>

      <p className="text-[11px] mt-2 leading-snug" style={{ color: isDark ? 'rgba(255,255,255,0.65)' : 'rgba(26,26,26,0.6)' }}>
        <strong style={{ color: accent }}>{left}</strong>{' '}
        place{left > 1 ? 's' : ''} restante{left > 1 ? 's' : ''} {scarcity.period} ·{' '}
        <span style={{ opacity: 0.7 }}>{scarcity.taken} déjà prise{scarcity.taken > 1 ? 's' : ''}</span>
      </p>
    </div>
  );
};

const AddonCard = ({ checked, onChange, label, desc, price, hidePrice = false, isDark, testId }) => {
  const accentBorder = checked ? COL.orange : (isDark ? 'rgba(255,255,255,0.10)' : 'rgba(26,26,26,0.10)');
  const bg = checked
    ? (isDark ? 'linear-gradient(180deg, rgba(240,90,26,0.18) 0%, rgba(240,90,26,0.05) 100%)' : 'linear-gradient(180deg, rgba(240,90,26,0.10) 0%, rgba(240,90,26,0.02) 100%)')
    : (isDark ? 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(0,0,0,0.10) 100%)' : '#fff');

  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className="relative text-left rounded-2xl p-4 transition-all overflow-hidden group hover:-translate-y-0.5"
      style={{
        background: bg,
        border: `1.5px solid ${accentBorder}`,
        boxShadow: checked
          ? '0 12px 28px -10px rgba(240,90,26,0.30), 0 0 0 1px rgba(240,90,26,0.15) inset'
          : (isDark ? '0 6px 20px -10px rgba(0,0,0,0.40)' : '0 4px 14px -8px rgba(26,26,26,0.10)'),
      }}
      data-testid={testId}
      aria-pressed={checked}
    >
      {/* Subtle stacked-card hint (peek behind, top-right) */}
      <span
        aria-hidden="true"
        className="absolute -top-1 -right-1 w-10 h-10 rounded-2xl opacity-40 transition-opacity group-hover:opacity-70"
        style={{
          background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(26,26,26,0.04)',
          border: `1px solid ${accentBorder}`,
          transform: 'rotate(8deg)',
          zIndex: 0,
        }}
      />

      <div className="relative flex items-start justify-between gap-3 mb-2">
        <p
          className="text-[12px] font-black uppercase tracking-[2px] leading-tight"
          style={{ color: checked ? COL.orange : (isDark ? '#fff' : COL.black) }}
        >
          {label}
        </p>

        {/* State indicator: checkmark when active, plus when inactive */}
        <span
          className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center transition-all"
          style={{
            background: checked ? COL.orange : 'transparent',
            border: `1.5px solid ${checked ? COL.orange : accentBorder}`,
            color: checked ? '#fff' : (isDark ? 'rgba(255,255,255,0.55)' : 'rgba(26,26,26,0.45)'),
          }}
        >
          {checked ? <Check className="w-3.5 h-3.5" strokeWidth={3} /> : (
            <span className="text-[14px] leading-none font-thin">+</span>
          )}
        </span>
      </div>

      <p
        className="relative text-[11.5px] leading-snug mb-3"
        style={{ color: isDark ? 'rgba(255,255,255,0.65)' : 'rgba(26,26,26,0.62)' }}
      >
        {desc}
      </p>

      {/* Bottom pill: hidden price → "Sur validation", otherwise show price */}
      <div className="relative flex items-center justify-between">
        <span
          className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-[2px]"
          style={{
            background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(26,26,26,0.05)',
            color: isDark ? 'rgba(255,255,255,0.70)' : 'rgba(26,26,26,0.65)',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'rgba(26,26,26,0.08)'}`,
          }}
        >
          {hidePrice ? 'Sur validation' : price}
        </span>
        {checked && (
          <span
            className="text-[9px] font-black uppercase tracking-[2px]"
            style={{ color: COL.orange }}
          >
            ✓ Activée
          </span>
        )}
      </div>
    </button>
  );
};

export default PricingTabs;
